import { geocodeSearch } from './geocoding';
import { clearDeliveryLocation, saveDeliveryLocation } from './storage';
import { syncStoreLocatorLabels } from './labels';

function getDrawer() {
  return document.getElementById('store-locator-delivery-drawer');
}

function getElements(drawer) {
  return {
    form: drawer.querySelector('.js-store-locator-delivery-form'),
    input: drawer.querySelector('.js-store-locator-delivery-input'),
    clearButton: drawer.querySelector('.js-store-locator-delivery-clear'),
    confirmButton: drawer.querySelector('.js-store-locator-delivery-confirm'),
    error: drawer.querySelector('.js-store-locator-delivery-error'),
    loading: drawer.querySelector('.js-store-locator-delivery-loading'),
  };
}

function setLoading(elements, isLoading) {
  elements.loading.hidden = !isLoading;
  elements.confirmButton.disabled = isLoading;
}

function setError(elements, message) {
  if (!message) {
    elements.error.hidden = true;
    elements.error.textContent = '';
    return;
  }

  elements.error.hidden = false;
  elements.error.textContent = message;
}

function updateConfirmState(elements) {
  const hasValue = elements.input.value.trim().length > 0;
  elements.confirmButton.disabled = !hasValue || !elements.loading.hidden;
}

function resetDrawerState(drawer) {
  const elements = getElements(drawer);
  elements.input.value = '';
  setError(elements, null);
  setLoading(elements, false);
  updateConfirmState(elements);
}

async function handleConfirm(drawer) {
  const elements = getElements(drawer);
  const query = elements.input.value.trim();

  setError(elements, null);

  if (!query) {
    setError(elements, 'Enter a valid postcode or suburb.');
    return;
  }

  setLoading(elements, true);

  try {
    const geocodeResult = await geocodeSearch(query);

    if (!geocodeResult) {
      setError(elements, 'Enter a valid postcode or suburb.');
      return;
    }

    const delivery = {
      query,
      formatted: geocodeResult.formatted,
      postcode: geocodeResult.postcode,
      latitude: geocodeResult.latitude,
      longitude: geocodeResult.longitude,
    };

    saveDeliveryLocation(delivery);
    syncStoreLocatorLabels();

    document.dispatchEvent(
      new CustomEvent('store-locator:delivery-changed', {
        bubbles: true,
        detail: delivery,
      })
    );

    drawer.close();
  } catch (confirmError) {
    console.error('Store locator delivery confirm failed:', confirmError);
    setError(elements, 'Something went wrong. Please try again.');
  } finally {
    setLoading(elements, false);
    updateConfirmState(elements);
  }
}

export function clearDeliverySelection() {
  clearDeliveryLocation();
  syncStoreLocatorLabels();

  document.dispatchEvent(
    new CustomEvent('store-locator:delivery-cleared', {
      bubbles: true,
    })
  );
}

export function initDeliverySelector() {
  const drawer = getDrawer();

  if (!drawer || drawer.dataset.storeLocatorDeliveryInit) return;

  drawer.dataset.storeLocatorDeliveryInit = 'true';
  const elements = getElements(drawer);

  elements.input.addEventListener('input', () => {
    setError(elements, null);
    updateConfirmState(elements);
  });

  elements.clearButton?.addEventListener('click', () => {
    elements.input.value = '';
    elements.input.focus();
    setError(elements, null);
    updateConfirmState(elements);
  });

  elements.form.addEventListener('submit', (event) => {
    event.preventDefault();
    handleConfirm(drawer);
  });

  drawer.addEventListener('on:store-locator-delivery:after-close', () => {
    resetDrawerState(drawer);
  });

  updateConfirmState(elements);
}

export function openDeliverySelector(opener) {
  initDeliverySelector();
  getDrawer()?.open(opener);
}
